import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, deleteDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '../Context/UserContext';
import { PERMISSIONS } from '../Utils/permissions';
import { openOrderWhatsAppEg } from '../Utils/whatsapp';
import { AUDIT_ACTIONS, buildAuditActor, logAuditEvent } from '../firebase/auditLogs';
import toast from 'react-hot-toast';
import './Admin.css';
import './OrderDetailsSplit.css';
import './OrderDetailsFinal.css';

export default function OrderDetailsFinal() {
  const { orderId } = useParams();
  const { role, user, userData, permissions } = useUser();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [shippingCompanies, setShippingCompanies] = useState([]);
  const navigate = useNavigate();

  const canManageOrders = !!permissions?.[PERMISSIONS.ORDERS_UPDATE];
  const canManageDelivery = !!permissions?.[PERMISSIONS.DELIVERY_UPDATE];
  const isShippingCompany = role === 'shipping_company';

  const auditActor = buildAuditActor({ user, role, userData });

  useEffect(() => {
    fetchOrderDetails();
    // Admin + Moderator (orders_update) can assign shipping
    if (canManageOrders) {
      fetchShippingCompanies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, role, canManageOrders]);

  useEffect(() => {
    if (order && (role === 'shipping_company' || role === 'shipping') && user) {
      if (order.assignedTo !== user.uid) {
        toast.error('You are not authorized to view this order');
        navigate('/admin/delivery');
      }
    }
  }, [order, role, user, navigate]);

  const fetchShippingCompanies = async () => {
    try {
      const q = query(collection(db, 'users'), where('role', 'in', ['shipping_company', 'shipping']));
      const querySnapshot = await getDocs(q);
      const companies = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setShippingCompanies(companies);
    } catch (error) {
      console.error('Error fetching shipping companies:', error);
    }
  };

  const assignToShipping = async (userId) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, {
        assignedTo: userId
      });
      setOrder(prev => ({ ...prev, assignedTo: userId }));

      await logAuditEvent({
        action: AUDIT_ACTIONS.ORDER_ASSIGN_SHIPPING,
        targetType: 'order',
        targetId: orderId,
        targetLabel: orderId,
        details: { assignedTo: userId },
        actor: auditActor
      });

      toast.success('Order assigned to shipping company');
    } catch (error) {
      console.error('Error assigning order:', error);
      toast.error('Failed to assign order');
    }
  };

  useEffect(() => {
    if (order && !editFormData.customerName) {
      setEditFormData(order);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  const fetchOrderDetails = async () => {
    try {
      const orderDoc = doc(db, 'orders', orderId);
      const orderSnapshot = await getDoc(orderDoc);
      
      if (orderSnapshot.exists()) {
        const orderData = {
          id: orderSnapshot.id,
          ...orderSnapshot.data()
        };
        setOrder(orderData);
        setEditFormData(orderData);
      } else {
        console.error('Order not found');
        navigate('/admin/orders');
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      navigate('/admin/orders');
    } finally {
      setLoading(false);
    }
  };

  const markAsDelivered = async () => {
    try {
      if (isShippingCompany) {
        toast.error('You are not allowed to mark as delivered');
        return;
      }

      if (!canManageOrders && !canManageDelivery) {
        toast.error('You are not allowed to update delivery status');
        return;
      }

      // Always deduct stock for delivered orders
      if (order.items && order.items.length > 0) {
        console.log('Deducting stock for delivered order');
        for (const item of order.items) {
          if (item.id && item.quantity) {
            const productRef = doc(db, 'products', item.id);
            const productDoc = await getDoc(productRef);
            
            if (productDoc.exists()) {
              const productData = productDoc.data();
              const currentStock = productData.stock || 0;
              const itemQuantity = item.quantity || 1;
              
              // Only deduct if we have enough stock
              if (currentStock >= itemQuantity) {
                await updateDoc(productRef, {
                  stock: currentStock - itemQuantity
                });
                
                console.log(`Deducted ${itemQuantity} units from product ${item.id} for delivery. New stock: ${currentStock - itemQuantity}`);
              } else {
                console.log(`Not enough stock for product ${item.id}. Current: ${currentStock}, Needed: ${itemQuantity}`);
                toast.warning(`Not enough stock for product. Available: ${currentStock}, Needed: ${itemQuantity}`);
              }
            }
          }
        }
      }
      
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, {
        status: 'delivered',
        deliveredAt: new Date().toISOString()
      });
      
      setOrder(prev => ({
        ...prev,
        status: 'delivered',
        deliveredAt: new Date().toISOString()
      }));

      await logAuditEvent({
        action: AUDIT_ACTIONS.ORDER_MARK_DELIVERED,
        targetType: 'order',
        targetId: orderId,
        targetLabel: orderId,
        details: { status: 'delivered' },
        actor: auditActor
      });

      toast.success('Order marked as delivered - stock deducted successfully');
    } catch (error) {
      console.error('Error marking order as delivered:', error);
      if (error?.code === 'permission-denied') {
        toast.error('Permission denied: cannot mark as delivered');
      } else {
        toast.error('Failed to update order');
      }
    }
  };

  const markAsShipped = async () => {
    try {
      if (!canManageDelivery) {
        toast.error('You are not allowed to mark as shipped');
        return;
      }

      const orderRef = doc(db, 'orders', orderId);
      const shippedAt = new Date().toISOString();
      await updateDoc(orderRef, {
        status: 'shipped',
        shippedAt
      });

      setOrder((prev) => ({
        ...prev,
        status: 'shipped',
        shippedAt
      }));

      await logAuditEvent({
        action: AUDIT_ACTIONS.ORDER_MARK_SHIPPED,
        targetType: 'order',
        targetId: orderId,
        targetLabel: orderId,
        details: { status: 'shipped', shippedAt },
        actor: auditActor
      });

      toast.success('Order marked as shipped');
    } catch (error) {
      console.error('Error marking order as shipped:', error);
      if (error?.code === 'permission-denied') {
        toast.error('Permission denied: cannot mark as shipped');
      } else {
        toast.error('Failed to update order');
      }
    }
  };

  const markAsPaid = async () => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, {
        paymentStatus: 'paid'
      });
      
      setOrder(prev => ({
        ...prev,
        paymentStatus: 'paid'
      }));

      await logAuditEvent({
        action: AUDIT_ACTIONS.ORDER_MARK_PAID,
        targetType: 'order',
        targetId: orderId,
        targetLabel: orderId,
        details: { paymentStatus: 'paid' },
        actor: auditActor
      });

      toast.success('Order marked as paid');
    } catch (error) {
      console.error('Error marking order as paid:', error);
      toast.error('Failed to update payment status');
    }
  };

  const markAsConfirmed = async () => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      const confirmedAt = new Date().toISOString();
      await updateDoc(orderRef, {
        status: 'confirmed',
        confirmedAt
      });
      setOrder(prev => ({
        ...prev,
        status: 'confirmed',
        confirmedAt
      }));

      await logAuditEvent({
        action: AUDIT_ACTIONS.ORDER_MARK_CONFIRMED,
        targetType: 'order',
        targetId: orderId,
        targetLabel: orderId,
        details: { status: 'confirmed', confirmedAt },
        actor: auditActor
      });

      toast.success('Order marked as confirmed');
    } catch (error) {
      console.error('Error confirming order:', error);
      toast.error('Failed to confirm order');
    }
  };

  const markAsCancelled = async () => {
    try {
      if (!canManageOrders) {
        toast.error('You are not allowed to cancel orders');
        return;
      }

      // Only restore stock if order was previously delivered
      if (order.status === 'delivered' && order.items && order.items.length > 0) {
        console.log('Order was delivered, restoring stock for cancellation');
        for (const item of order.items) {
          console.log('Processing item:', item);
          if (item.id && item.quantity) {
            const productRef = doc(db, 'products', item.id);
            const productDoc = await getDoc(productRef);
            
            if (productDoc.exists()) {
              const productData = productDoc.data();
              const currentStock = productData.stock || 0;
              const itemQuantity = item.quantity || 1;
              
              console.log(`Product ${item.id}: Current stock: ${currentStock}, Item quantity: ${itemQuantity}`);
              
              await updateDoc(productRef, {
                stock: currentStock + itemQuantity
              });
              
              const newStock = currentStock + itemQuantity;
              console.log(`Restored ${itemQuantity} units to product ${item.id}. New stock: ${newStock}`);
            } else {
              console.log(`Product ${item.id} not found`);
            }
          } else {
            console.log(`Invalid item:`, item);
          }
        }
      } else {
        console.log('Order was not delivered, no stock to restore');
      }
      
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, {
        status: 'cancelled',
        paymentStatus: 'refunded',
        cancelledAt: new Date().toISOString()
      });
      
      setOrder(prev => ({
        ...prev,
        status: 'cancelled',
        paymentStatus: 'refunded',
        cancelledAt: new Date().toISOString()
      }));

      await logAuditEvent({
        action: AUDIT_ACTIONS.ORDER_MARK_CANCELLED,
        targetType: 'order',
        targetId: orderId,
        targetLabel: orderId,
        details: { status: 'cancelled', paymentStatus: 'refunded' },
        actor: auditActor
      });

      toast.success('Order cancelled and stock restored successfully');
    } catch (error) {
      console.error('Error cancelling order:', error);
      if (error?.code === 'permission-denied') {
        toast.error('Permission denied: cannot cancel order');
      } else {
        toast.error('Failed to cancel order');
      }
    }
  };

  const deleteOrder = async () => {
    if (window.confirm('Are you sure you want to delete this order? This action cannot be undone.')) {
      try {
        console.log('User role:', role);
        console.log('Attempting to delete order:', orderId);

        if (!canManageOrders) {
          toast.error('You are not allowed to delete orders');
          return;
        }

        await logAuditEvent({
          action: AUDIT_ACTIONS.ORDER_DELETE,
          targetType: 'order',
          targetId: orderId,
          targetLabel: orderId,
          actor: auditActor
        });
        
        await deleteDoc(doc(db, 'orders', orderId));
        console.log('Order deleted successfully');
        toast.success('Order deleted successfully');
        navigate('/admin/orders');
      } catch (error) {
        console.error('Error deleting order:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        
        // More specific error messages
        if (error.code === 'permission-denied') {
          toast.error('Permission denied. You do not have access to delete orders.');
        } else if (error.code === 'not-found') {
          toast.error('Order not found');
        } else {
          toast.error('Failed to delete order');
        }
      }
    }
  };

  const handleEditChange = (field, value) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
  };

  // eslint-disable-next-line no-unused-vars
  const saveEdit = async () => {
    try {
      // Calculate new total
      const productPrice = editFormData.items?.[0]?.price || 0;
      const shippingFee = editFormData.shippingFee || 0;
      const newTotal = productPrice + shippingFee;
      
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, {
        ...editFormData,
        totalAmount: newTotal,
        status: editFormData.status || order.status,
        paymentStatus: editFormData.paymentStatus || order.paymentStatus
      });
      
      setOrder({
        ...editFormData,
        totalAmount: newTotal,
        status: editFormData.status || order.status,
        paymentStatus: editFormData.paymentStatus || order.paymentStatus
      });
      setIsEditing(false);
      toast.success('Order updated successfully');
    } catch (error) {
      console.error('Error saving order:', error);
      toast.error('Failed to save order');
    }
  };

  // eslint-disable-next-line no-unused-vars
  const cancelEdit = () => {
    setEditFormData({...order});
    setIsEditing(false);
  };

  const openWhatsApp = () => {
    const res = openOrderWhatsAppEg(order);
    if (!res?.ok) {
      toast.error('لا يوجد رقم هاتف للعميل');
    }
  };

  if (loading) {
    return (
      <div className="admin-content">
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="error-container">
        <div className="error-message">Order not found</div>
        <button onClick={() => navigate('/admin/orders')} className="back-btn">
          Back to Orders
        </button>
      </div>
    );
  }

  const total = order.totalAmount || 0;
  const paid = order.paidAmount || 0;
  // eslint-disable-next-line no-unused-vars
  const balance = total - paid;

  return (
    <div className="order-details-final">
        {/* Header */}
        <div className="order-header-split">
          <div className="header-left">
            <button onClick={() => navigate('/admin/orders')} className="back-btn">
              <ion-icon name="arrow-back-outline"></ion-icon>
              Orders
            </button>
            <div className="order-title">
              <h1>Order #{order.id.slice(-8)}</h1>
              <div className="order-meta">
                <span className={`status-badge ${order.status === 'delivered' ? 'fulfilled' : 'unfulfilled'}`}>
                  {order.status === 'delivered' ? 'Fulfilled' : 'Unfulfilled'}
                </span>
                <span className="order-date">
                  {order.createdAt?.toDate ? 
                    new Date(order.createdAt.toDate()).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    }) : 
                    new Date(order.createdAt).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })
                  }
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="order-content-split">
          {/* Left Column - Customer Info Only */}
          <div className="order-column">
            <div className="order-section">
              <div className="section-header">
                <h2>Customer</h2>
                <div className="order-number">#{order.id.slice(-8)}</div>
              </div>
              
              {/* Customer Info */}
              <div className="customer-block">
                <div className="customer-info">
                  <div className="info-row">
                    <div className="info-item">
                      <label>Customer Name</label>
                      <div className="info-value">{order.customerName}</div>
                    </div>
                  </div>
                  <div className="info-row">
                    <div className="info-item">
                      <label>Phone Number</label>
                      <div className="info-value">{order.customerPhone}</div>
                    </div>
                  </div>
                  <div className="info-row">
                    <div className="info-item">
                      <label>Order Date</label>
                      <div className="info-value">
                        {order.createdAt?.toDate ? 
                          new Date(order.createdAt.toDate()).toLocaleDateString('en-US', {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric'
                          }) : 
                          new Date(order.createdAt).toLocaleDateString('en-US', {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric'
                          })
                        }
                      </div>
                    </div>
                  <div className="info-row">
                    <div className="info-item">
                      <label>Shipping Address</label>
                      <div className="info-value">{order.customerAddress || 'No address'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Products + Actions */}
          <div className="fulfillment-column">
            <div className="fulfillment-section">
              <div className="section-header">
                <h2>Products</h2>
                <div className="fulfillment-status">
                  <span className="status-dot unfulfilled"></span>
                  {order.items?.length || 0} items
                </div>
              </div>

              {/* Products List */}
              <div className="fulfillment-items">
                {order.items?.map((item, index) => (
                  <div key={index} className="fulfillment-item">
                    <div className="item-image" style={{ width: '60px', height: '60px', minWidth: '60px', borderRadius: '6px', overflow: 'hidden', flexShrink: 0 }}>
                      <img 
                        src={item.imageUrl || item.image || '/placeholder.png'} 
                        alt={item.name} 
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => {e.target.onerror = null; e.target.src = '/placeholder.png'}}
                      />
                    </div>
                    <div className="item-details">
                      <div className="item-name">{item.name}</div>
                      <div className="item-quantity">
                        <span style={{ fontWeight: '600', color: '#374151' }}>Qty: {item.quantity || 1}</span>
                        <span style={{ margin: '0 8px', color: '#d1d5db' }}>|</span>
                        <span>EGP {item.price}</span>
                      </div>
                      {item.selectedTaste && (
                        <div className="item-taste">Taste: {item.selectedTaste}</div>
                      )}
                    </div>
                    <div className="item-total">
                      EGP {(item.price * (item.quantity || 1)).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Order Summary */}
              <div className="order-summary-section">
                <h3>Order Summary</h3>
                <div className="status-display">
                  <div className="status-item">
                    <label>Order Status</label>
                    <span className={`status-badge ${order.status === 'delivered' ? 'delivered' : order.status === 'cancelled' ? 'cancelled' : (order.status === 'confirmed' || order.status === 'processing' || order.status === 'shipped') ? 'fulfilled' : 'pending'}`}>
                      {order.status === 'delivered' ? 'Delivered' : order.status === 'cancelled' ? 'Cancelled' : order.status === 'confirmed' ? 'Confirmed' : order.status === 'processing' ? 'Processing' : order.status === 'shipped' ? 'Shipped' : 'Pending'}
                    </span>
                  </div>
                  <div className="status-item">
                    <label>{order.status === 'cancelled' ? 'Order' : 'Payment'} Status</label>
                    <span className={`status-badge ${order.paymentStatus === 'paid' ? 'paid' : order.paymentStatus === 'refunded' ? 'refunded' : 'pending'}`}>
                      {order.paymentStatus === 'paid' ? 'Paid' : order.paymentStatus === 'refunded' ? 'Refunded' : 'Pending'}
                    </span>
                  </div>
                  <div className="status-item">
                    <label>Payment Method</label>
                    <span className="status-badge" style={{ background: '#f3f4f6', color: '#374151' }}>
                      {order.paymentMethod === 'cod' ? 'Cash on Delivery' : 
                       order.paymentMethod === 'vodafone_cash' ? 'Vodafone Cash' : 
                       order.paymentMethod === 'instapay' ? 'InstaPay' : 
                       order.paymentMethod || 'Unknown'}
                    </span>
                  </div>
                </div>

                {/* Payment Evidence Button */}
                {(order.paymentMethod === 'vodafone_cash' || order.paymentMethod === 'instapay') && order.paymentEvidenceUrl && (
                  <div style={{ marginTop: '15px', marginBottom: '15px' }}>
                    <a 
                      href={order.paymentEvidenceUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="action-btn"
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        gap: '8px',
                        textDecoration: 'none',
                        backgroundColor: '#f0f9ff',
                        color: '#0284c7',
                        borderColor: '#0284c7',
                        width: '100%',
                        boxSizing: 'border-box'
                      }}
                    >
                      <ion-icon name="image-outline"></ion-icon>
                      View Payment Screenshot
                    </a>
                  </div>
                )}

                {isEditing ? (
                  <div className="summary-edit-form">
                    <div className="edit-row">
                      <label>Product Price</label>
                      <input
                        type="number"
                        value={editFormData.items?.[0]?.price || ''}
                        onChange={(e) => {
                          const newItems = [...(editFormData.items || [])];
                          if (newItems[0]) {
                            newItems[0].price = parseFloat(e.target.value) || 0;
                            handleEditChange('items', newItems);
                          }
                        }}
                        className="edit-input small"
                      />
                    </div>
                    <div className="edit-row">
                      <label>Shipping Fee</label>
                      <input
                        type="number"
                        value={editFormData.shippingFee || ''}
                        onChange={(e) => handleEditChange('shippingFee', parseFloat(e.target.value) || 0)}
                        className="edit-input small"
                      />
                    </div>
                    <div className="edit-row">
                      <label>Total Amount</label>
                      <span>EGP {((editFormData.items?.[0]?.price || 0) + (editFormData.shippingFee || 0)).toFixed(2)}</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="summary-row">
                      <span>Product Price</span>
                      <span>EGP {order.items?.[0]?.price || 0}</span>
                    </div>
                    <div className="summary-row">
                      <span>Shipping Fee</span>
                      <span>EGP {order.shippingFee || 0}</span>
                    </div>
                    <div className="summary-row total">
                      <span>Total Amount</span>
                      <span>EGP {total}</span>
                    </div>
                  </>
                )}
              </div>

              {/* Order Actions */}
              {(canManageOrders || canManageDelivery) && (
                <div className="order-actions-section">
                  <h3>Order Actions</h3>
                  
                  {/* Admin + Moderator: Assign Shipping */}
                  {canManageOrders && (
                    <div className="shipping-assignment" style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Assign to Shipping Company</label>
                        <div className="assign-row" style={{ display: 'flex', gap: '10px' }}>
                            <select 
                                value={order.assignedTo || ''} 
                                onChange={(e) => {
                                    if (e.target.value) {
                                        assignToShipping(e.target.value);
                                    }
                                }}
                                className="shipping-select"
                                style={{ 
                                    padding: '8px 12px', 
                                    borderRadius: '6px', 
                                    border: '1px solid #e2e8f0',
                                    flex: 1
                                }}
                            >
                                <option value="">Select Company</option>
                                {shippingCompanies.map(company => (
                                    <option key={company.id} value={company.id}>
                                        {company.name || company.displayName || company.email || 'Unknown Company'}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                  )}

                  <div className="action-buttons-grid">
                    {/* Admin + Moderator: Mark as Confirmed */}
                    {canManageOrders && order.status !== 'confirmed' && order.status !== 'cancelled' && order.status !== 'delivered' && (
                      <button
                        className="action-btn"
                        onClick={markAsConfirmed}
                        disabled={isEditing}
                        style={{ borderColor: '#2563eb', color: '#2563eb' }}
                      >
                        <ion-icon name="checkmark-circle-outline"></ion-icon>
                        Mark as Confirmed
                      </button>
                    )}

                    {/* Admin + Moderator: Mark as Paid */}
                    {canManageOrders && order.paymentStatus !== 'paid' && (
                      <button 
                        className="action-btn"
                        onClick={markAsPaid}
                        style={{ borderColor: '#059669', color: '#059669' }}
                      >
                        <ion-icon name="card-outline"></ion-icon>
                        Mark as Paid
                      </button>
                    )}

                    {/* Shipping Company: Mark as Shipped ONLY */}
                    {isShippingCompany ? (
                      <button
                        className={`action-btn ${order.status === 'shipped' ? 'active' : ''}`}
                        onClick={markAsShipped}
                        disabled={order.status === 'shipped' || order.status === 'delivered' || order.status === 'cancelled' || isEditing}
                        style={{ borderColor: '#8b5cf6', color: '#8b5cf6' }}
                      >
                        <ion-icon name="send-outline"></ion-icon>
                        {order.status === 'shipped' ? 'Shipped' : 'Mark as Shipped'}
                      </button>
                    ) : (
                      <>
                        {/* Shipping + Admin/Moderator: Mark as Delivered */}
                        <button 
                          className={`action-btn ${order.status === 'delivered' ? 'active' : ''}`}
                          onClick={markAsDelivered}
                          disabled={order.status === 'delivered' || isEditing}
                        >
                          <ion-icon name="home-outline"></ion-icon>
                          {order.status === 'delivered' ? 'Delivered' : 'Mark as Delivered'}
                        </button>

                        {/* WhatsApp (not for shipping_company) */}
                        <button 
                          className="action-btn"
                          onClick={openWhatsApp}
                          style={{ borderColor: '#25D366', color: '#25D366' }}
                        >
                          <ion-icon name="logo-whatsapp"></ion-icon>
                          Chat on WhatsApp
                        </button>
                      </>
                    )}
                    
                    {/* Admin + Moderator: Cancel */}
                    {canManageOrders && (
                        <button 
                        className={`action-btn danger ${order.status === 'cancelled' ? 'active' : ''}`}
                        onClick={markAsCancelled}
                        disabled={order.status === 'cancelled' || isEditing}
                        >
                        <ion-icon name="close-circle-outline"></ion-icon>
                        {order.status === 'cancelled' ? 'Cancelled' : 'Cancel Order'}
                        </button>
                    )}
                  </div>

                  {/* Admin + Moderator: Delete */}
                  {canManageOrders && (
                    <div className="admin-actions">
                        <h4>Admin Actions</h4>
                        <div className="admin-buttons">
                        <button className="admin-btn delete" onClick={deleteOrder}>
                            <ion-icon name="trash-outline"></ion-icon>
                            Delete Order
                        </button>
                        </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
